import { useState } from 'react';
import { Typography, List, Paper, Divider, Stack, Button } from '@mui/material';

import PlayerItem from './PlayerItem';
import { Player, Position, Team, TeamName } from '../types';
import { DragAndDropWithoutContext } from './DragAndDrop';
import TeamJerseyColor from './TeamJerseyColor';
import { CustomTeamNameDialog } from './CustomTeamNameDialog';

type TeamProps = {
  team: Team;
  canMoveTo: TeamName[];
  onDeletePlayer?: (player: string, fromTeam: TeamName) => void;
  onMovePlayer?: (player: string, fromTeam: TeamName, toTeam: TeamName) => void;
  onSetPlayerPosition?: (player: Player, position?: Position) => void;
  onDragPlayerEnd?: (fromIndex: number, toIndex: number, team: Team) => void;
  onSetCustomTeamName?: (team: Team, customName: string) => void;
  hidePositions: boolean;
};

function getDroppableStyle(isDroppingOver: boolean) {
  return {
    height: '100%',
    background: isDroppingOver ? '#81c784' : 'inherit',
  };
}

export default function TeamCard({
  team,
  onDeletePlayer,
  onMovePlayer,
  onSetPlayerPosition,
  onSetCustomTeamName,
  canMoveTo,
  hidePositions,
}: Readonly<TeamProps>) {
  const [showTeamNameDialog, setShowTeamNameDialog] = useState(false);

  const handleDelete = (player: string) => {
    onDeletePlayer && onDeletePlayer(player, team.name);
  };
  const handleMove = (player: string, toTeam: TeamName) => {
    onMovePlayer && onMovePlayer(player, team.name, toTeam);
  };

  const handleSetPlayerPosition = (player: Player, position?: Position) => {
    onSetPlayerPosition && onSetPlayerPosition(player, position);
  };

  const handleNameDialogSubmit = (customName: string) => {
    setShowTeamNameDialog(false);
    team.customName = customName;
    onSetCustomTeamName && onSetCustomTeamName(team, customName);
  };

  return (
    <Paper
      elevation={4}
      sx={{
        padding: 2,
        display: 'flex',
        flexDirection: 'column',
        mb: {
          xs: 3,
          sm: 3,
          md: 3,
          lg: 0,
          xl: 0,
        },
        minWidth: {
          lg: '320px',
        },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Button
          variant="text"
          size="small"
          sx={{
            color: 'rgb(0, 0, 0, 0.87)',
            textTransform: 'none',
          }}
          onClick={() => setShowTeamNameDialog(true)}
        >
          <Typography
            variant="button"
            sx={{ fontSize: 18, textTransform: 'none' }}
          >
            Team {team.name} {team.customName ? `- ${team.customName}` : ''}
          </Typography>
        </Button>
        <TeamJerseyColor team={team} />
      </Stack>
      <Divider />
      <List
        style={{ marginLeft: '-16px', marginRight: '-16px', height: '100%' }}
      >
        <DragAndDropWithoutContext<Player>
          droppableId={team.name}
          draggableItems={team.players}
          getDraggableId={(player) => player.name}
          getDroppableStyle={getDroppableStyle}
          itemRenderer={(item, isDragging, dragHandleProps) => (
            <PlayerItem
              player={item}
              canMoveTo={canMoveTo.filter((teamName) => teamName !== team.name)}
              onDelete={handleDelete}
              onMove={handleMove}
              onSetPosition={handleSetPlayerPosition}
              dragHandleProps={dragHandleProps}
              isDragging={isDragging}
              hidePosition={hidePositions}
            />
          )}
        />
      </List>
      {showTeamNameDialog && (
        <CustomTeamNameDialog
          currentTeamName={team.customName}
          onClose={() => setShowTeamNameDialog(false)}
          onSubmit={handleNameDialogSubmit}
        />
      )}
    </Paper>
  );
}
